import React from "react";
import {
  Event,
  Identifiable,
  List,
  Milestone,
  Project,
  ProjectPortfolio,
  ProjectReport,
  Resource,
  CalendarHolidays,
  Task,
  Unavailability,
} from "@planzone/dto";
import { Filter as F } from "@planzone/dto";
import { Props, DataType } from "./types";
import { isProjectFiltered } from "./projects";
import { isResourceFiltered } from "./resources";
import { isProjectReportFiltered } from "./projectReport";
import { isListFiltered } from "./lists";
import { isTaskFiltered } from "./tasks";
import { isEventFiltered } from "./events";
import { isMilestoneFiltered } from "./milestones";
import { isProjectPortfolioFiltered } from "./projectPortfolios";
import { isCalendarHolidaysFiltered } from "./calendarHolidays";
import { isUnavailabilityFiltered } from "./unavailability";

const filterMap: { [Key in DataType]: (data: any, filter: F) => boolean } = {
  project: isProjectFiltered,
  list: isListFiltered,
  task: isTaskFiltered,
  event: isEventFiltered,
  milestone: isMilestoneFiltered,
  resource: isResourceFiltered,
  projectReport: isProjectReportFiltered,
  projectPortfolio: isProjectPortfolioFiltered,
  calendarHolidays: isCalendarHolidaysFiltered,
  unavailability: isUnavailabilityFiltered,
};

function filter<Data extends Array<any>>(
  data: Data,
  filter: F,
  extractCriteria: (data: Data[number]) => {
    project?: Identifiable<Project>;
    list?: Identifiable<List>;
    task?: Identifiable<Task>;
    event?: Identifiable<Event>;
    milestone?: Identifiable<Milestone>;
    resource?: Identifiable<Resource>;
    projectReport?: Identifiable<ProjectReport>;
    projectPortfolio?: Identifiable<ProjectPortfolio>;
    calendarHolidays?: Identifiable<CalendarHolidays>;
    unavailability?: Identifiable<Unavailability>;
  },
) {
  return data.filter((data) => {
    const criteria = extractCriteria(data);

    return !Object.entries(criteria).reduce(
      (isFiltered, [key, value]) =>
        isFiltered
          ? isFiltered
          : filterMap[key as keyof typeof filterMap]
            ? filterMap[key as keyof typeof filterMap](value, filter)
            : isFiltered,
      false,
    );
  }) as Data;
}

function Filter<Data extends Array<any>>(props: Props<Data>) {
  return (
    <React.Fragment>
      {props.children(filter(props.data, props.filter, props.extractCriteria))}
    </React.Fragment>
  );
}

export { filter, Filter };
